import type {
  WorkflowGateMode,
  WorkflowLayout,
  WorkflowStepDef,
  WorkflowView,
} from '@/lib/api/generated/model';

import {
  CANVAS_PAD,
  LANE_HEADER_HEIGHT,
  NODE_GAP,
  PHASE_ACCENT_COUNT,
  PHASE_PAD,
  ROW_TOP,
  STEP_NODE_HEIGHT,
  STEP_NODE_WIDTH,
  SYSTEM_NODE_HEIGHT,
  SYSTEM_NODE_WIDTH,
  autoLayout,
  bandLayout,
  toCanvas,
} from '../builder/layout';
import type { Band, LayoutInput } from '../builder/layout';
import {
  DONE_NODE_ID,
  START_NODE_ID,
  UNASSIGNED_PHASE_ID,
} from '../builder/types';
import type { NodeId, Point } from '../builder/types';
import { phaseNodeId, transitionEdgeId } from '../builder/selection';
import { resolveTransitionTarget } from './selection';

/**
 * Pure view-model for the workflow graph, shared by the read-only page and the
 * builder (research D5).
 *
 * Everything here is arithmetic over the workflow definition — no React, no
 * canvas library — so the result is deterministic by construction and testable
 * without rendering anything.
 *
 * **What changed when the builder arrived.** This module used to walk a single
 * chain: the first step was the one nothing targeted, and each step's one
 * outgoing transition named its successor. That walk is gone. Branching and
 * looping are the shapes real workflows take, and a chain walk cannot express
 * either — it would render a branch as a line and loop forever on a cycle.
 *
 * Positions now come from the stored arrangement, or from `autoLayout` when
 * there is none. Both canvases call the same geometry, which is what stops them
 * from disagreeing about a workflow nobody has arranged yet (FR-057).
 */

export {
  LANE_HEADER_HEIGHT,
  NODE_GAP,
  PHASE_ACCENT_COUNT,
  PHASE_PAD,
  STEP_NODE_HEIGHT,
  STEP_NODE_WIDTH,
  SYSTEM_NODE_HEIGHT,
  SYSTEM_NODE_WIDTH,
};

/**
 * Node data must be plain records: the canvas library constrains node `data` to
 * `Record<string, unknown>`, which type aliases satisfy structurally.
 */
export type SystemNodeData = {
  name: string;
  role: 'start' | 'done';
};

export type StepNodeData = {
  name: string;
  stepType: 'ai_step' | 'human_gate' | 'linked_workflow';
  mode: WorkflowGateMode | null;
  llm: string | null;
  prompt: string;
  /**
   * The target workflow's display name, resolved from the step's `workflow`
   * key against the workspace's workflow list — `null` for a step that isn't
   * `linked_workflow`, or whose target key doesn't resolve (a broken
   * reference, shown as such rather than omitted; research.md D6/D7).
   */
  linkedWorkflowName: string | null;
  phaseId: string;
  phaseName: string;
  /** Index into the token layer's phase accents — see globals.css. */
  accentIndex: number;
  /**
   * How many transitions leave this step.
   *
   * Carried in the model rather than counted in the renderer because it is only
   * visible on the canvas as arrows — which is exactly what a screen-reader user
   * cannot see, and what tells a dead end from a branch (FR-054).
   */
  outgoing: number;
  /**
   * Marked as the problem the user just jumped to from a refused publication
   * (FR-008).
   *
   * Optional and set only by the builder, which is the only canvas with
   * problems to report. The read-only graph leaves it unset, so one renderer
   * still serves both (FR-057).
   */
  flagged?: boolean;
  /**
   * An AI step with no AI profile, or one that no longer exists (spec 144
   * FR-007). It saves; it will not publish until one is chosen.
   *
   * Set only by the builder, like `flagged`: the read-only graph shows what
   * is published, which cannot have one.
   */
  needsProfile?: boolean;
};

export type PhaseNodeData = {
  name: string;
  stepCount: number;
  accentIndex: number;
  /** The reserved holding phase reads differently: it is not a stage. */
  unassigned: boolean;
};

type GraphNodeBase = {
  id: string;
  position: Point;
  width: number;
  height: number;
};

export type GraphNode =
  | (GraphNodeBase & { kind: 'system'; data: SystemNodeData })
  | (GraphNodeBase & { kind: 'step'; data: StepNodeData })
  | (GraphNodeBase & { kind: 'phase'; data: PhaseNodeData });

export type GraphEdge = {
  id: string;
  source: string;
  target: string;
  /**
   * Which of the edges between this pair this one is.
   *
   * Numbered here rather than in the renderer because an edge component only
   * sees itself, so it cannot know it has a twin — and both canvases need the
   * same numbering to separate coincident arrows the same way.
   */
  parallelIndex: number;
  /**
   * What the edge's chip reads, or `null` for an arrow that carries none.
   *
   * Not the condition, which is prose written for the runtime — cut to chip
   * width it identifies nothing, and two arrows out of one step read as two
   * sentence fragments.
   *
   * **Most arrows carry nothing**, which is the point. `NODE_GAP` is 72px, so
   * anything longer than about eight characters cannot fit between two
   * auto-laid-out nodes — a chip on every arrow is a row of clipped fragments
   * hidden behind the cards either side of it. An arrow earns one only when it
   * says something the arrow itself does not:
   *
   * - the author wrote a label, which is a deliberate act of naming and what
   *   the inspector's field promises ("Shown on the arrow"); or
   * - the step branches, and the key is then the only thing telling two arrows
   *   out of one card apart.
   *
   * A step with one way out and no label gets a bare arrow: "then this" is
   * already what an arrow means, and `analysis_complete` written across the gap
   * says no more than the line does. The key is still in the detail panel, in
   * full, next to its condition.
   *
   * The arrow out of Backlog never carries one either: it is synthesized from
   * the workflow's `start` rather than being a transition, so it has neither a
   * label nor a key.
   */
  label: string | null;
  /**
   * Whether {@link label} is the fallback key rather than the author's own
   * words. An identifier belongs in the mono face this product uses for
   * identifiers everywhere; a label the author wrote is prose and does not.
   */
  isIdentifier: boolean;
};

export type WorkflowGraphModel = {
  nodes: GraphNode[];
  edges: GraphEdge[];
  width: number;
  height: number;
};

/**
 * Build the view-model.
 *
 * `layout` is the service's stored arrangement in its `{ nodes: … }` envelope.
 * Absent for every imported workflow — `layout` is not in the YAML shape at all
 * — which is the normal case, not an error one.
 */
export function workflowToGraph(
  workflow: WorkflowView,
  layout?: WorkflowLayout | null,
  frozenWidths?: Readonly<Record<string, number>> | null,
  /**
   * Resolves a `linked_workflow` step's `workflow` key to its target's
   * display name. Optional and defaulted to "nothing resolves" rather than
   * required, so a caller with no workflow list in scope (yet) still compiles
   * — it just shows no name, the same as a genuinely broken reference.
   */
  resolveWorkflowName: (key: string) => string | null = () => null,
): WorkflowGraphModel {
  const phases = declaredPhases(workflow);
  const offsets = resolveOffsets(workflow, phases, layout);

  const input: LayoutInput = { phases, layout: offsets };
  const { bands, width, height } = bandLayout(input, frozenWidths);

  const bandById = new Map(bands.map((band) => [band.id, band]));
  const nodes: GraphNode[] = [];

  // Bands first, so the canvas draws them beneath the nodes they enclose.
  for (const band of bands) {
    if (band.kind !== 'phase' && band.kind !== 'unassigned') continue;
    // `Band.id` is nullable for the three implicit regions, and the two kinds
    // kept above are not among them — the holding pen carries
    // `UNASSIGNED_PHASE_ID` like any other. Stated rather than asserted past:
    // the old `` `phase:${band.id}` `` would have produced a node called
    // "phase:null" if this ever stopped holding.
    if (band.id === null) continue;
    const phase = phases.find((candidate) => candidate.id === band.id);
    nodes.push({
      id: phaseNodeId(band.id),
      kind: 'phase',
      position: band.origin,
      width: band.width,
      height: band.height,
      data: {
        name: band.name,
        stepCount: phase?.steps.length ?? 0,
        accentIndex: band.accentIndex,
        unassigned: band.kind === 'unassigned',
      },
    });
  }

  nodes.push(
    systemNode(
      workflow.start_step.id,
      workflow.start_step.name,
      'start',
      bandById.get(START_NODE_ID),
    ),
    ...phases.flatMap((phase) =>
      phase.steps.map((step) =>
        stepNode(
          step,
          phase,
          offsets,
          bands,
          accentOf(bands, phase.id),
          resolveWorkflowName,
        ),
      ),
    ),
    systemNode(
      workflow.done_step.id,
      workflow.done_step.name,
      'done',
      bandById.get(DONE_NODE_ID),
    ),
  );

  return {
    nodes: nodes.filter((node): node is GraphNode => node !== null),
    edges: edgesOf(workflow, phases),
    width,
    height,
  };
}

type DeclaredPhase = {
  id: string;
  name: string;
  steps: WorkflowStepDef[];
};

/**
 * The phases as the canvas draws them, with the system steps excluded.
 *
 * The contract says start and done are never phase members, and today's service
 * agrees — but the renderer's job is to survive whatever was persisted, not to
 * assume. Without this filter a backlog-kind step arriving inside a phase would
 * render as an AI step with an empty prompt and inflate the phase's count.
 */
function declaredPhases(workflow: WorkflowView): DeclaredPhase[] {
  return workflow.phases.map((phase) => ({
    id: phase.id,
    name: phase.id === UNASSIGNED_PHASE_ID ? 'Unassigned' : phase.name,
    steps: (phase.steps ?? []).filter(
      (step) => step.type !== 'backlog' && step.type !== 'done',
    ),
  }));
}

/**
 * The declared steps in the order the diagram lays them out: phase by phase, then
 * each phase's steps in turn, Backlog and Done left out (spec 281 FR-006).
 *
 * Read off `declaredPhases` rather than re-derived, so "next" in the prompt dialog
 * can never disagree with what the canvas draws to its right.
 */
export function promptReadingOrder(
  workflow: WorkflowView,
): { step: WorkflowStepDef; phaseName: string }[] {
  return declaredPhases(workflow).flatMap((phase) =>
    phase.steps.map((step) => ({ step, phaseName: phase.name })),
  );
}

/**
 * Stored offsets, or a computed arrangement when there are none.
 *
 * Partial arrangements are filled in rather than rejected: a step added by one
 * client and saved by another may have no entry, and the alternative to
 * defaulting it is not drawing it.
 */
function resolveOffsets(
  workflow: WorkflowView,
  phases: readonly DeclaredPhase[],
  layout: WorkflowLayout | null | undefined,
): Record<NodeId, Point> {
  const stored = layout?.nodes;
  if (!stored || Object.keys(stored).length === 0) {
    return autoLayout({ phases, start: workflow.start });
  }

  const offsets: Record<NodeId, Point> = {};
  for (const [id, point] of Object.entries(stored)) {
    if (point) offsets[id] = { x: point.x, y: point.y };
  }

  const missing = phases.flatMap((phase) =>
    phase.steps.filter((step) => !(step.id in offsets)),
  );
  if (missing.length > 0) {
    const computed = autoLayout({ phases, start: workflow.start });
    for (const step of missing) offsets[step.id] = computed[step.id];
  }

  return offsets;
}

function accentOf(bands: readonly Band[], phaseId: string): number {
  return bands.find((band) => band.id === phaseId)?.accentIndex ?? 0;
}

function systemNode(
  id: string,
  name: string,
  role: 'start' | 'done',
  band: Band | undefined,
): GraphNode {
  // Centred vertically against a step's height, so the two read as being on the
  // same line rather than the system node floating at the top of its band.
  const y = ROW_TOP + (STEP_NODE_HEIGHT - SYSTEM_NODE_HEIGHT) / 2;
  return {
    id,
    kind: 'system',
    position: {
      x: (band?.origin.x ?? CANVAS_PAD) + PHASE_PAD,
      y,
    },
    width: SYSTEM_NODE_WIDTH,
    height: SYSTEM_NODE_HEIGHT,
    data: { name, role },
  };
}

function stepNode(
  step: WorkflowStepDef,
  phase: DeclaredPhase,
  offsets: Readonly<Record<NodeId, Point>>,
  bands: readonly Band[],
  accentIndex: number,
  resolveWorkflowName: (key: string) => string | null,
): GraphNode {
  const position = toCanvas(step.id, phase.id, offsets, bands) ?? {
    x: CANVAS_PAD,
    y: ROW_TOP,
  };
  const stepType =
    step.type === 'human_gate'
      ? 'human_gate'
      : step.type === 'linked_workflow'
        ? 'linked_workflow'
        : 'ai_step';

  return {
    id: step.id,
    kind: 'step',
    position,
    width: STEP_NODE_WIDTH,
    height: STEP_NODE_HEIGHT,
    data: {
      name: step.name,
      stepType,
      mode: step.mode ?? null,
      llm: step.llm ?? null,
      prompt: step.prompt ?? '',
      linkedWorkflowName:
        stepType === 'linked_workflow' && step.workflow
          ? resolveWorkflowName(step.workflow)
          : null,
      phaseId: phase.id,
      phaseName: phase.name,
      accentIndex,
      outgoing: (step.transitions ?? []).length,
    },
  };
}

/**
 * One edge per transition, plus the one leaving Start.
 *
 * The edge id is `` `${stepId}:${transition.key}` ``, which already supports
 * several edges leaving one step — that part needed no change for branching.
 *
 * The edge out of Start goes to the *declared* start step. Inferring it from
 * "the step nothing targets" is what stopped working once loops became legal:
 * in a workflow that cycles, nothing is untargeted.
 *
 * The chip text is resolved here rather than in either canvas. The builder used
 * to look each label back up out of its draft, which left the read-only page
 * drawing unlabelled arrows for the same workflow — the one thing this shared
 * mapper exists to prevent (FR-057).
 */
function edgesOf(
  workflow: WorkflowView,
  phases: readonly DeclaredPhase[],
): GraphEdge[] {
  const edges: GraphEdge[] = [];
  const allSteps = phases.flatMap((phase) => phase.steps);

  const startTarget =
    workflow.start && allSteps.some((step) => step.id === workflow.start)
      ? workflow.start
      : (allSteps[0]?.id ?? workflow.done_step.id);

  // How many edges already run between a given pair, so coincident ones can be
  // told apart by the renderer.
  const seen = new Map<string, number>();
  const nextIndex = (source: string, target: string): number => {
    const pair = `${source}->${target}`;
    const index = seen.get(pair) ?? 0;
    seen.set(pair, index + 1);
    return index;
  };

  edges.push({
    id: `${workflow.start_step.id}->${startTarget}`,
    source: workflow.start_step.id,
    target: startTarget,
    parallelIndex: nextIndex(workflow.start_step.id, startTarget),
    label: null,
    isIdentifier: false,
  });

  for (const step of allSteps) {
    const transitions = step.transitions ?? [];
    // Whether the key has any disambiguating to do. One way out needs no name.
    const branches = transitions.length > 1;

    for (const transition of transitions) {
      const target = resolveTransitionTarget(workflow, transition.to);
      // An empty label is no label: the inspector's field clears to `''`, and a
      // blank chip is worse than none at all.
      const authored = transition.label ? transition.label : null;
      const chip = authored ?? (branches ? transition.key : null);

      edges.push({
        id: transitionEdgeId(step.id, transition.key),
        source: step.id,
        target,
        parallelIndex: nextIndex(step.id, target),
        label: chip,
        isIdentifier: authored === null && chip !== null,
      });
    }
  }

  return edges;
}
